import {
  StatusBarColorsV4,
  StatusBarV4,
  StatusBarStatusColorsV4,
  BoxV4,
  ButtonV2,
  PlanType,
  LabraToolTipV2,
} from '@labrav/react-components'

import { CombinedFormState } from '../../../../modules/combinedForm/reducer'
import { useMemo, useState } from 'react'

import { faCheck, faClock, faLink } from '@fortawesome/pro-regular-svg-icons'

import { useStyles } from './StatusHeader.style'
import { Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ExtendOfferExpirationDateButton } from '../../../ExtendOfferExpirationDateModal/ExtendOfferExpirationDateModal'
import { productDetailDate } from '../../../../common/utils/productCreationUtil'
import {
  OfferStatus,
  OfferTypeRenew,
} from '../../../../modules/privateOffer/reducer'
import {
  getExpirationFromDate,
  getLocalDateTime,
} from '../../../../common/utils/dates'

import clsx from 'clsx'
import { copyToClipboard } from '@labrav/common'
import { useParams } from 'react-router'
import { offerSuccessMessage } from '../../../../common/utils/constants'

interface StatusHeaderProps {
  combinedFormData: CombinedFormState
  viewingPersona: string
}

const TruncatedErrorMessage = ({ errorMessage }: { errorMessage: string }) => {
  if (errorMessage.length > 100) {
    const truncatedError = errorMessage.substring(0, 100) + '...'
    return (
      <LabraToolTipV2 description={errorMessage} tooltipPlacement="bottom">
        {truncatedError}
      </LabraToolTipV2>
    )
  }
  return errorMessage
}

export default function StatusHeader({
  combinedFormData,
  viewingPersona = 'CP',
}: StatusHeaderProps) {
  let status: JSX.Element | null = null
  const { classes } = useStyles()
  const [copiedOfferLink, setCopiedOfferLink] = useState(false)
  const { partnerId: partnerIdFromURL } = useParams<{
    partnerId?: string
  }>()

  const getRejectedDateTime = (formRejectedAt: string) => {
    const rejectedDate = getLocalDateTime(
      formRejectedAt as string
    ).formattedDate
    const rejectedTime = getLocalDateTime(
      formRejectedAt as string
    ).formattedTime

    return `${rejectedDate} | ${rejectedTime}`
  }

  const getFailedDateTime = (
    offerFailedAt?: string,
    agreementFailedAt?: string
  ) => {
    let failedDate = '-'
    let failedTime = '-'
    if (agreementFailedAt) {
      failedDate = getLocalDateTime(offerFailedAt as string).formattedDate
      failedTime = getLocalDateTime(offerFailedAt as string).formattedTime
    } else if (offerFailedAt) {
      failedDate = getLocalDateTime(offerFailedAt as string).formattedDate
      failedTime = getLocalDateTime(offerFailedAt as string).formattedTime
    }
    return { formattedFailedDate: failedDate, formattedFailedTime: failedTime }
  }
  const { formattedFailedDate, formattedFailedTime } = getFailedDateTime(
    combinedFormData.cppoData.offerAuditJson?.offerFailedAt,
    combinedFormData.resellerAgreementData.agreementAuditJson
      ?.resaleAuthCreationFailedAt
  )
  const copyOfferLink = async () => {
    if (!combinedFormData.cppoData.cloudOfferUrl) {
      console.error('No offer link found')
      return
    }
    try {
      await copyToClipboard(combinedFormData.cppoData.cloudOfferUrl)
      setCopiedOfferLink(true)
      setTimeout(() => {
        setCopiedOfferLink(false)
      }, 2000)
    } catch (error) {
      console.error('Error copying URL to clipboard:', error)
    }
  }
  const { isExpriringSoon, dayDiff, isExpired } = getExpirationFromDate(
    combinedFormData.cppoData.offerExpirationDate as string,
    5
  )

  const formattedAcceptedDate = combinedFormData.cppoData.offerAuditJson
    ?.offerAcceptedAt
    ? getLocalDateTime(
        combinedFormData.cppoData.offerAuditJson?.offerAcceptedAt
      ).formattedDate
    : '-'
  const formattedAcceptedTime = combinedFormData.cppoData.offerAuditJson
    ?.offerAcceptedAt
    ? getLocalDateTime(
        combinedFormData.cppoData.offerAuditJson?.offerAcceptedAt
      ).formattedTime
    : '-'

  const formattedViwedDate = combinedFormData.cppoData.offerAuditJson
    ?.offerViewedAt
    ? getLocalDateTime(combinedFormData.cppoData.offerAuditJson?.offerViewedAt)
        .formattedDate
    : '-'
  const formattedViwedTime = combinedFormData.cppoData.offerAuditJson
    ?.offerViewedAt
    ? getLocalDateTime(combinedFormData.cppoData.offerAuditJson?.offerViewedAt)
        .formattedTime
    : '-'
  const formattedSentDate = combinedFormData.cppoData.offerAuditJson
    ?.offerEmailSentAt
    ? getLocalDateTime(
        combinedFormData.cppoData.offerAuditJson?.offerEmailSentAt
      ).formattedDate
    : '-'
  const formattedSentTime = combinedFormData.cppoData.offerAuditJson
    ?.offerEmailSentAt
    ? getLocalDateTime(
        combinedFormData.cppoData.offerAuditJson?.offerEmailSentAt
      ).formattedTime
    : '-'

  const offerExpiringSoon =
    isExpriringSoon &&
    ![
      OfferStatus.PRIVATE_OFFER_ACCEPTED,
      OfferStatus.PRIVATE_OFFER_EXPIRED,
      OfferStatus.PRIVATE_OFFER_RESTRCITED,
      OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS,
      OfferStatus.PRIVATE_OFFER_CREATION_IN_PROGRESS,
      OfferStatus.PRIVATE_OFFER_FAILED,
    ].includes(combinedFormData.cppoData.offerStatus as OfferStatus)

  let fullDateExpirationTimeForHeader = ''

  const {
    modifiedDate: offerExpirationDateDisplay,
    modifiedTime: offerExpirationDateTime,
  } = combinedFormData.cppoData.offerExpirationDate
    ? productDetailDate(combinedFormData.cppoData.offerExpirationDate)
    : { modifiedDate: '', modifiedTime: '' }
  fullDateExpirationTimeForHeader = `${offerExpirationDateDisplay} ${offerExpirationDateTime}`

  type CombinedOfferStatus =
    | 'in_progress'
    | 'failed'
    | 'offer_created'
    | 'expiring_soon'
    | 'expired'
    | 'completed'
    | 'rejected'
    | 'sent'
    | 'viewed'
  const combinedOfferStatus = useMemo<CombinedOfferStatus | null>(() => {
    const cStatus = combinedFormData?.cppoData?.offerStatus
    const aStatus = combinedFormData?.resellerAgreementData?.agreementStatus
    const fStatus = combinedFormData?.status

    if (fStatus === 'REJECTED') return 'rejected'
    if (aStatus === 'channel_offer_accepted') return 'completed'
    if (isExpired) return 'expired'
    if (offerExpiringSoon) return 'expiring_soon'

    if (fStatus === 'APPROVED') {
      if (combinedFormData.labraResellerAgreementId !== null) {
        if (combinedFormData.labraCppoId !== null) {
          if (cStatus === OfferStatus.PRIVATE_OFFER_CREATION_IN_PROGRESS)
            return 'in_progress'
          if (cStatus === OfferStatus.PRIVATE_OFFER_EMAIL_SENT) return 'sent'
          if (cStatus === OfferStatus.PRIVATE_OFFER_ACCEPTED) return 'completed'
          if (cStatus === OfferStatus.PRIVATE_OFFER_EXPIRED) return 'expired'
          if (cStatus === OfferStatus.PRIVATE_OFFER_VIEWED) return 'viewed'
          if (cStatus === OfferStatus.PRIVATE_OFFER_FAILED) return 'failed'
        } else {
          if (aStatus === 'sent') return 'sent'
          else if (aStatus === 'expired') return 'expired'
          else if (aStatus === 'in_progress') return 'in_progress'
          else if (aStatus === 'completed') return 'completed'
          else if (aStatus === 'failed') return 'failed'
        }
      }
    } else {
      if (fStatus === 'REJECTED') return 'rejected'
    }
    return null
  }, [combinedFormData, isExpired, offerExpiringSoon])

  const dateForValidation = () => {
    const installments = combinedFormData.cppoData.installmentInfo
    const sortedDates = installments
      .map(item => new Date(item.paymentDate))
      .sort((a, b) => a.getTime() - b.getTime())
    if (sortedDates.length > 1) {
      const secondInstallmentDate = sortedDates[1]

      if (combinedFormData.cppoData.variableStartDate) {
        return secondInstallmentDate.toISOString().split('T')[0]
      }

      const agreementDateObj = new Date(
        combinedFormData.cppoData.agreementStartDate as string
      )
      if (agreementDateObj.getTime() === secondInstallmentDate.getTime()) {
        return agreementDateObj.toISOString().split('T')[0]
      }

      const smallerDate =
        agreementDateObj < secondInstallmentDate
          ? agreementDateObj
          : secondInstallmentDate
      return smallerDate.toISOString().split('T')[0]
    }
  }
  const ExpirationDateButton = () => {
    return (
      <ExtendOfferExpirationDateButton
        offerExpirationDate={
          combinedFormData.cppoData.offerExpirationDate || '1990-12-12'
        }
        offerId={combinedFormData.labraCppoId as string}
        planType={
          combinedFormData.resellerAgreementData.productType as PlanType
        }
        offerSubscriptionEndDate={
          combinedFormData.cppoData.subscriptionEndDate as string
        }
        offerStatus={combinedFormData.cppoData.offerStatus as OfferStatus}
        isDraft={false}
        agreementStartDate={
          combinedFormData.resellerAgreementData.productType ===
          PlanType.SAAS_CONTRACT
            ? dateForValidation()
            : combinedFormData.cppoData.agreementStartDate
        }
        type={'NEW' as OfferTypeRenew}
        extendOfferType="CPPO"
        updateOfferStatusCheck={false}
        updateOfferExpiringToolTipMessage={''}
        classes={{
          updateButtonContainer: classes.updateButtonContainer,
          disabledEdit: classes.disabledEdit,
          extendOfferExpiry: classes.extendOfferExpiry,
        }}
        partnerIdFromURL={partnerIdFromURL}
        successMessageText={offerSuccessMessage}
        limitForExpirationDate={
          combinedFormData.cppoData.installmentInfo.length > 1 ? true : false
        }
      />
    )
  }

  switch (combinedOfferStatus) {
    case 'rejected':
      status = (
        <StatusBarV4
          statusColor={StatusBarStatusColorsV4.RED}
          backgroundColor={StatusBarColorsV4.RED}
          status="Rejected"
          divider={true}
          content={
            <div className={classes.fullWidthStatusBar}>
              <Typography className={classes.statusText}>
                This offer has been rejected by the ISV.
              </Typography>
              <Typography className={classes.greyedOut}>
                {getRejectedDateTime(
                  combinedFormData.auditLogs?.REJECTED?.[0]?.at || ''
                )}
              </Typography>
            </div>
          }
        />
      )
      break
    case 'in_progress':
      status = (
        <StatusBarV4
          statusColor={StatusBarStatusColorsV4.GREEN}
          status="In Progress"
          backgroundColor={StatusBarColorsV4.LIGHTBLUE}
          divider
          content={
            <div className={classes.fullWidthStatusBar}>
              <Typography className={classes.statusText}>
                The offer is being created in the AWS Marketplace and will be
                ready in approximately 5 minutes. Please refresh the page
                periodically to check the status.
              </Typography>
            </div>
          }
        />
      )
      break
    case 'failed':
      status = (
        <StatusBarV4
          statusColor={StatusBarStatusColorsV4.RED}
          backgroundColor={StatusBarColorsV4.RED}
          status="Failed"
          divider={true}
          content={
            <div className={classes.fullWidthStatusBar}>
              <Typography className={classes.statusText}>
                <TruncatedErrorMessage
                  errorMessage={`Offer creation has failed on AWS ${
                    combinedFormData.resellerAgreementData
                      .agreementErrorMessage ||
                    combinedFormData.cppoData.offerErrorMessage ||
                    '-'
                  }`}
                />
              </Typography>
              <Typography className={classes.greyedOut}>
                {formattedFailedDate} | {formattedFailedTime}
              </Typography>
            </div>
          }
        />
      )
      break
    case 'completed':
      status = (
        <StatusBarV4
          backgroundColor={StatusBarColorsV4.GREEN}
          statusColor={StatusBarStatusColorsV4.GREEN}
          status="Accepted"
          divider
          content={
            <div className={classes.fullWidthStatusBar}>
              <Typography className={classes.statusText}>
                Offer was accepted on {formattedAcceptedDate},
                {formattedAcceptedTime}
              </Typography>
            </div>
          }
        />
      )
      break
    case 'expiring_soon':
      status = (
        <StatusBarV4
          backgroundColor={StatusBarColorsV4.RED}
          statusColor={StatusBarStatusColorsV4.RED}
          status=""
          content={
            <div className={classes.fullWidthStatusBar}>
              <Typography className={classes.statusText}>
                <FontAwesomeIcon
                  icon={faClock}
                  style={{
                    width: '16px',
                    height: '16px',
                    fontSize: '16px',
                    padding: '0px 12px 0px 0px',
                  }}
                />
                Offer expiring in {dayDiff} days. Expires at{' '}
                {fullDateExpirationTimeForHeader}
              </Typography>
            </div>
          }
        />
      )
      break
    case 'expired':
      status = (
        <StatusBarV4
          backgroundColor={StatusBarColorsV4.RED}
          statusColor={StatusBarStatusColorsV4.RED}
          status="Expired"
          divider
          content={
            <div
              className={clsx(
                classes.fullWidthStatusBar,
                classes.fullWidthStatusBarWithExpiration
              )}
            >
              <Typography className={classes.statusText}>
                Offer expired on {}
                {fullDateExpirationTimeForHeader}
              </Typography>
              {viewingPersona === 'CP' ? (
                <div className={classes.expirationContainer}>
                  <ExpirationDateButton />
                </div>
              ) : null}
            </div>
          }
        />
      )
      break
    case 'sent':
      status = (
        <StatusBarV4
          backgroundColor={StatusBarColorsV4.LIGHTBLUE}
          statusColor={StatusBarStatusColorsV4.GREEN}
          status="Sent"
          divider
          content={
            <div className={classes.fullWidthStatusBar}>
              <Typography className={classes.statusText}>
                Offer was sent on {formattedSentDate},{formattedSentTime}
              </Typography>
            </div>
          }
        />
      )
      break
    case 'viewed':
      status = (
        <StatusBarV4
          statusColor={StatusBarStatusColorsV4.GREEN}
          backgroundColor={StatusBarColorsV4.LIGHTBLUE}
          status="Viewed"
          divider
          content={
            <div className={classes.fullWidthStatusBar}>
              <Typography className={classes.statusText}>
                Offer was viewed on {formattedViwedDate},{formattedViwedTime}
              </Typography>
            </div>
          }
        />
      )
      break
    default:
      status = null
      break
  }
  const ShowExtendExpiryDate = () => {
    const ShowExtendExpiryDateButton = [
      'offer_created',
      'expiring_soon',
      'viewed',
      'sent',
    ].includes(combinedOfferStatus as CombinedOfferStatus as string)
    if (ShowExtendExpiryDateButton) {
      return <ExpirationDateButton />
    }
    return null
  }

  const ShowCopyLink = () => {
    const showCopyLinkButton = [
      'offer_created',
      'expiring_soon',
      'viewed',
      'sent',
    ].includes(combinedOfferStatus as CombinedOfferStatus as string)
    if (showCopyLinkButton) {
      return (
        <LabraToolTipV2
          description={`${copiedOfferLink ? 'Copied' : ''}`}
          tooltipPlacement="top"
          icon={
            <FontAwesomeIcon
              icon={faCheck}
              style={{ width: '12px', height: '12px' }}
            />
          }
          tooltipPlacmentGap="0"
        >
          <ButtonV2
            className={classes.extendOfferExpiry}
            styleType="link"
            data-testid={'extend-expiration-date'}
            onClick={copyOfferLink}
            disabled={!combinedFormData.cppoData.cloudOfferUrl}
          >
            <FontAwesomeIcon
              icon={faLink}
              style={{
                width: '16px',
                height: '16px',
                fontSize: '16px',
                padding: '0px 12px 0px 0px',
                color: '#1942C5',
              }}
            />
            Offer link
          </ButtonV2>
        </LabraToolTipV2>
      )
    }
    return null
  }

  const StatusBarFooter = () => {
    if (
      viewingPersona === 'CP' &&
      !['failed', 'in_progress', 'expired', 'rejected'].includes(
        combinedOfferStatus as CombinedOfferStatus
      )
    ) {
      return (
        <div className={classes.offerLinkRoot}>
          <div className={classes.updateButtonContainer}>
            <ShowCopyLink />
          </div>
          <ShowExtendExpiryDate />
        </div>
      )
    }
    return null
  }
  if (viewingPersona === 'ISV' && combinedOfferStatus === 'offer_created') {
    return null
  }
  return (
    <>
      <BoxV4 variant="small">
        <div className={classes.statusHeaderRoot}>
          {combinedOfferStatus !== 'offer_created' ? (
            <div style={{ width: '100%' }}> {status} </div>
          ) : null}
          <StatusBarFooter />
        </div>
      </BoxV4>
    </>
  )
}
